import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

export interface OnboardingChecklist {
  id: number
  added_to_notion: boolean
  added_to_slack: boolean
  added_to_google_group: boolean
  added_to_pod: boolean
  sent_onboarding_email: boolean
  sent_welcome_aboard_email: boolean
  added_to_notion_at: string | null
  added_to_slack_at: string | null
  added_to_google_group_at: string | null
  added_to_pod_at: string | null
  sent_onboarding_email_at: string | null
  sent_welcome_aboard_email_at: string | null
  updated_at: string
}

export interface OnboardingUser {
  id: number
  first_name: string
  last_name: string
  email: string
  username: string
  avatar_image: string | null
  slack_member_id: string | null
  checklist: OnboardingChecklist
}

export interface OnboardingPod {
  id: number
  name: string
}

export interface LatestPodResponse {
  pod: OnboardingPod | null
  users: OnboardingUser[]
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const notOnboardedUsers = ref<OnboardingUser[]>([])
  const latestPodUsers = ref<OnboardingUser[]>([])
  const latestPod = ref<OnboardingPod | null>(null)
  const pods = ref<OnboardingPod[]>([])
  const isLoadingNotOnboarded = ref(false)
  const isLoadingLatestPod = ref(false)
  const isLoadingPods = ref(false)
  const error = ref<string | null>(null)

  async function fetchNotOnboarded() {
    isLoadingNotOnboarded.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.ONBOARDING_NOT_ONBOARDED)
      notOnboardedUsers.value = response.data
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Failed to fetch users'
      console.error('Error fetching not-onboarded users:', err)
    } finally {
      isLoadingNotOnboarded.value = false
    }
  }

  async function fetchLatestPodUsers() {
    isLoadingLatestPod.value = true
    error.value = null
    try {
      const response = await api.get<LatestPodResponse>(API_ROUTES.ONBOARDING_LATEST_POD)
      latestPod.value = response.data.pod
      latestPodUsers.value = response.data.users
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Failed to fetch latest pod users'
      console.error('Error fetching latest pod users:', err)
    } finally {
      isLoadingLatestPod.value = false
    }
  }

  async function fetchPods() {
    isLoadingPods.value = true
    try {
      const response = await api.get<OnboardingPod[]>(API_ROUTES.ONBOARDING_PODS)
      pods.value = response.data
    } catch (err: any) {
      console.error('Error fetching pods:', err)
    } finally {
      isLoadingPods.value = false
    }
  }

  async function updateChecklist(userId: number, data: Partial<Omit<OnboardingChecklist, 'id' | 'updated_at'>>) {
    try {
      const response = await api.patch<OnboardingChecklist>(API_ROUTES.ONBOARDING_CHECKLIST(userId), data)
      _patchLocalChecklist(userId, response.data)
      return response.data
    } catch (err: any) {
      console.error('Error updating checklist:', err)
      throw err
    }
  }

  async function sendWelcomeEmail(userId: number) {
    const response = await api.post<OnboardingChecklist>(
      API_ROUTES.ONBOARDING_SEND_WELCOME_EMAIL(userId),
    )
    _patchLocalChecklist(userId, response.data)
    return response.data
  }

  async function updateSlackId(userId: number, slackMemberId: string) {
    try {
      await api.patch(API_ROUTES.USER_UPDATE_BY_ID(userId), { slack_member_id: slackMemberId })
      const patchUser = (list: OnboardingUser[]) => {
        const user = list.find((u) => u.id === userId)
        if (user) user.slack_member_id = slackMemberId
      }
      patchUser(notOnboardedUsers.value)
      patchUser(latestPodUsers.value)
    } catch (err: any) {
      console.error('Error updating Slack member ID:', err)
      throw err
    }
  }

  async function assignPod(userIds: number[], podId: number) {
    try {
      await api.post(API_ROUTES.ONBOARDING_ASSIGN_POD, { user_ids: userIds, pod_id: podId }, { timeout: 120000 })
    } catch (err: any) {
      console.error('Error assigning pod:', err)
      throw err
    }
  }

  function _patchLocalChecklist(userId: number, updatedChecklist: OnboardingChecklist) {
    const inNotOnboarded = notOnboardedUsers.value.find((u) => u.id === userId)
    if (inNotOnboarded) inNotOnboarded.checklist = updatedChecklist

    const inLatestPod = latestPodUsers.value.find((u) => u.id === userId)
    if (inLatestPod) inLatestPod.checklist = updatedChecklist
  }

  return {
    notOnboardedUsers,
    latestPodUsers,
    latestPod,
    pods,
    isLoadingNotOnboarded,
    isLoadingLatestPod,
    isLoadingPods,
    error,
    fetchNotOnboarded,
    fetchLatestPodUsers,
    fetchPods,
    updateChecklist,
    sendWelcomeEmail,
    updateSlackId,
    assignPod,
  }
})
